import ServiceDirectory from '@/components/ServiceDirectory';
import searchContent from '@/lib/search-content.json';
import {Questions} from '@/components/Editorial';
import {Action,Section,inquiry} from '@/components/Marketing';
import React from "react";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Stamp,
  FileBadge,
} from "lucide-react";
import {
  TECH_CAPABILITIES,
  TECH_EQUIPMENT,
  SEALED_MODEL_BENEFITS,
  MEDIA,
} from "@/lib/site-data";

export default function Technology() {
  useDocumentMeta(SEO_ROUTES["/technology"]);
  return (
    <div data-testid="technology-page" className="w-full">
      {/* Hero */}
      <section className="relative py-24 md:py-32 border-b border-zinc-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-65"
          style={{
            backgroundImage: `url('${MEDIA.opTwilightTech}')`,
            backgroundPosition: "65% 50%",
            filter: "brightness(0.78) contrast(1.08) saturate(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/15 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <span className="inline-block w-2 h-2 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
                Construction Technology
              </span>
            </div>
            <h1
              data-testid="technology-title"
              className="font-display uppercase text-[clamp(2.8rem,8vw,7rem)] leading-[0.88] font-black tracking-tight"
            >
              Machine Control.
              <br />
              <span className="metallic-text">Mastered in the Field.</span>
            </h1>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-3xl">
              GPS machine control, robotic total stations, local positioning
              systems, and site calibration — deployed, calibrated, and
              trained by someone who has actually run the technology on
              heavy civil jobsites. We integrate the stack modern earthwork
              and paving operations run on.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="py-24 border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="flex flex-col gap-4">
              <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
                Core capabilities
              </span>
              <h2 className="font-display uppercase text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight max-w-3xl">
                What We Deploy, Calibrate, and Train.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm">
              Whether you're running a single dozer or a full paving train,
              we set it up to perform and train the crews to own it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {TECH_CAPABILITIES.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.title}
                  data-testid={`tech-capability-${cap.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                  className="bg-[#141417] p-8 md:p-10 flex flex-col gap-5 min-h-[280px] border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-colors duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border border-[#ff6b1a]/40 flex items-center justify-center">
                      <Icon size={20} className="text-[#ff6b1a]" />
                    </div>
                    <span className="font-display text-xs tracking-[0.3em] uppercase text-zinc-600">
                      Capability {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-2xl font-bold tracking-tight leading-tight">
                    {cap.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {cap.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* In the field — visual band */}
      <section
        data-testid="tech-visual-band"
        className="border-b border-zinc-900"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <figure className="relative aspect-[4/3] md:aspect-auto md:min-h-[640px] overflow-hidden bg-black group">
            <img
              src={MEDIA.techCalibration}
              alt="Robotic total station calibrated on subgrade with motor grader working in the background"
              className="absolute inset-0 w-full h-full object-cover transition-transform [transition-duration:1200ms] ease-out group-hover:scale-[1.03]"
              style={{ objectPosition: "50% 55%" }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-[#ff6b1a]/0 group-hover:ring-[#ff6b1a]/40 transition-all duration-500" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-8 md:p-10 flex flex-col gap-3">
              <span className="inline-flex items-center gap-2 self-start text-[10px] uppercase tracking-[0.3em] text-[#ff6b1a] border border-[#ff6b1a]/40 bg-black/60 px-2 py-1">
                Site Calibration
              </span>
              <h3 className="font-display uppercase text-2xl md:text-3xl font-bold tracking-tight text-white">
                Calibrated on the Ground.
              </h3>
              <p className="text-zinc-300 text-sm max-w-md leading-relaxed">
                Robotic total stations set, shot, and verified against the
                grade — so the model your operators are running actually
                matches the dirt.
              </p>
            </figcaption>
          </figure>
          <figure className="relative aspect-[4/3] md:aspect-auto md:min-h-[640px] overflow-hidden bg-black group">
            <img
              src={MEDIA.techTraining}
              alt="Field crew running a Topcon total station onsite during training"
              className="absolute inset-0 w-full h-full object-cover transition-transform [transition-duration:1200ms] ease-out group-hover:scale-[1.03]"
              style={{ objectPosition: "55% 35%" }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-[#ff6b1a]/0 group-hover:ring-[#ff6b1a]/40 transition-all duration-500" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-8 md:p-10 flex flex-col gap-3">
              <span className="inline-flex items-center gap-2 self-start text-[10px] uppercase tracking-[0.3em] text-[#ff6b1a] border border-[#ff6b1a]/40 bg-black/60 px-2 py-1">
                Field Training
              </span>
              <h3 className="font-display uppercase text-2xl md:text-3xl font-bold tracking-tight text-white">
                Trained Until It Sticks.
              </h3>
              <p className="text-zinc-300 text-sm max-w-md leading-relaxed">
                Total stations, base stations, rovers — your crews learn the
                tech hands-on, on real ground, until it's second nature.
              </p>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Featured field clip */}
      <section
        data-testid="tech-video-feature"
        className="relative border-b border-zinc-900 bg-black"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <span className="inline-block w-2 h-2 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
                In motion
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight">
              Tech That Runs
              <br />
              <span className="metallic-text">At Production Speed.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-md">
              When the calibration is right and the operators trust the
              model, the equipment moves. This is what that looks like.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="relative aspect-video overflow-hidden border border-zinc-800 bg-[#141417]">
              <video
                data-testid="tech-feature-video"
                controls
                playsInline
                preload="metadata"
                poster={MEDIA.fieldClipPoster}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={MEDIA.fieldClipMp4} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment supported */}
      <section className="py-20 bg-[#070708] border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
              Equipment supported
            </span>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight">
              From Blade to Paver.
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-md">
              Machine control and survey integration across the full heavy
              civil fleet. If it runs on a model, we can set it up to
              perform.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ul
              data-testid="tech-equipment-list"
              className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900"
            >
              {TECH_EQUIPMENT.map((item, i) => (
                <li
                  key={item}
                  className="bg-[#141417] px-6 py-5 flex items-center gap-4"
                >
                  <span className="font-display text-sm text-[#ff6b1a]/70 w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display uppercase tracking-[0.12em] text-[14px] text-white">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="forge-site"><Section eyebrow="Support Available by Appointment" title="Troubleshoot the Problem. Define the Next Step." body="Bring your machine-control system, equipment details, symptoms, and relevant screenshots or files. We review the issue, agree on the paid consultation or support scope, and schedule remote or onsite work."><div className="forge-actions"><Action to={inquiry('Machine control troubleshooting')}>Request Troubleshooting</Action><Action to="/construction-models-takeoffs-cut-fill" secondary>Models, Takeoffs & Cut/Fill</Action></div><p className="section-note">{searchContent.consultationTerms}</p></Section></div>
      {/* Model deliverables are quoted to the project scope. */}
      <section className="relative py-24 border-b border-zinc-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{
            backgroundImage: `url('${MEDIA.opDrillPour}')`,
            backgroundPosition: "50% 50%",
            filter: "brightness(0.7) contrast(1.08) saturate(1.0)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 items-start mb-14">
            <div className="col-span-12 lg:col-span-7 flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <span className="inline-block w-2 h-2 bg-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
                  The advantage
                </span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl font-bold leading-[0.95] tracking-tight">
                Field-Ready
                <br />
                <span className="metallic-text">Construction Models.</span>
              </h2>
              <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl">
                ForgedOps offers construction model building, quantity takeoffs, earthwork quantities, and cut/fill analysis. Share current plans, models, source files, and the outputs you need for review and a quote. Turnaround depends on complexity, file readiness, and the agreed scope.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="border border-[#ff6b1a]/40 bg-[#141417] p-8 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 border border-[#ff6b1a]/60 flex items-center justify-center">
                    <Stamp size={20} className="text-[#ff6b1a]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                      Model Integrity
                    </div>
                    <div className="font-display text-xl font-bold tracking-tight">
                      Prepared · Reviewed · Verified
                    </div>
                  </div>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Model scope, review responsibilities, and field verification requirements are defined for the project. Professional sealing is arranged where required and included in the agreed scope.
                </p>
                <div className="flex items-center gap-2 text-[#ff6b1a] text-sm">
                  <FileBadge size={14} /> Project-specific requirements
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {SEALED_MODEL_BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`sealed-model-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#141417] p-8 md:p-10 flex flex-col gap-4"
                >
                  <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center">
                    <Icon size={20} className="text-[#ff6b1a]" />
                  </div>
                  <h3 className="font-display uppercase text-xl font-bold tracking-tight">
                    {b.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {b.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <span className="font-display uppercase tracking-[0.35em] text-xs text-[#ff6b1a]">
              How we roll it out
            </span>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight">
              Setup. <br />
              Calibrate. <br />
              <span className="metallic-text">Train. Support.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Technology only earns its keep if it's deployed right, calibrated
              correctly, and trusted by the operators running it.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ul className="flex flex-col">
              {[
                "Assessment: current workflow, equipment, and survey stack",
                "Procurement guidance: the right gear for your real operation",
                "Setup & base station configuration",
                "Site calibration — done right, documented, defensible",
                "Operator & foreman training onsite",
                "Ongoing support, troubleshooting, and optimization",
              ].map((line, i) => (
                <li
                  key={line}
                  className="flex items-start gap-4 border-b border-zinc-900 py-5"
                >
                  <span className="font-display text-2xl font-bold text-[#ff6b1a]/70 w-12 flex-shrink-0">
                    0{i + 1}
                  </span>
                  <div className="flex items-start gap-3 pt-1">
                    <CheckCircle2
                      size={16}
                      className="text-[#ff6b1a] mt-1 flex-shrink-0"
                    />
                    <span className="text-zinc-200 text-base">{line}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="forge-site"><Questions items={searchContent.faqs["/technology"]}/></div>
      <ServiceDirectory group="technology"/>

      {/* CTA */}
      <section className="py-24 bg-[#070708] border-t border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-8 flex flex-col gap-4">
            <h3 className="font-display uppercase text-3xl md:text-5xl font-bold tracking-tight leading-[0.95]">
              Ready to Make Your Machine Control Actually Perform?
            </h3>
            <p className="text-zinc-400 text-base md:text-lg max-w-2xl">
              From base station setup to model implementation — tell us
              where your tech is underperforming. We'll scope it and make it
              work.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/contact?interest=Machine%20control%20%26%20field%20technology"
              data-testid="technology-cta-primary"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-[#ff8533] transition-colors duration-200"
            >
              Talk to Us
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <Link
              to="/systems"
              data-testid="technology-cta-secondary"
              className="inline-flex items-center gap-3 px-7 py-4 border border-zinc-600 text-white font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-zinc-900 hover:border-zinc-300 transition-all duration-300"
            >
              See Our Systems
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


